import { Box, Divider, ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { v2MenuItemSizeClass } from '@onevision-resources/design-theme';
import { CalendarClock, Gauge, GraduationCap, Megaphone, MessagesSquare } from 'lucide-react';
import { useState } from 'react';
import { usePartners } from 'src/graphql';
import { useGlobalStore } from 'src/store';
import { FramePopover } from './FramePopover';

const DEFAULT_LINKS = {
  metrics: 'https://metrics.onevisionresources.com/login',
  scheduleMeeting: 'https://calendly.com/onevision-partner-success',
};

export const SupportLinks = ({
  anchor,
  onCloseMenu,
}: {
  anchor: HTMLElement | null;
  onCloseMenu?: () => void;
}) => {
  const { partners } = usePartners();

  const [frameDialog, setFrameDialog] = useState({
    isOpen: false,
    title: '',
    url: '',
  });

  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const selectedPartner = selectedPartnerId ? partners?.[selectedPartnerId]! : null;
  const slackTeamLink = selectedPartner?.slackTeamLink?.trim() ?? '';

  const navigateToExternalUrl = (url?: string) => {
    if (!url) return;

    const urlWithProtocol = url.includes('https://') ? url : `https://${url}`;

    return window.open(urlWithProtocol, '_blank');
  };

  const items: {
    name: string;
    icon: typeof MessagesSquare;
    shouldShow: boolean | string | undefined;
    onClick: () => void;
    keepMenuOpen?: boolean;
  }[] = [
    {
      name: 'Slack',
      icon: MessagesSquare,
      shouldShow: Boolean(slackTeamLink),
      onClick: () => navigateToExternalUrl(slackTeamLink),
    },
    {
      name: 'Metrics',
      icon: Gauge,
      shouldShow: true,
      onClick: () => navigateToExternalUrl(DEFAULT_LINKS.metrics),
    },
    {
      name: 'Marketing Assets',
      icon: Megaphone,
      shouldShow: selectedPartner?.notionBoardUrl,
      onClick: () => navigateToExternalUrl(selectedPartner?.notionBoardUrl),
    },
    {
      name: 'Onboarding Resources',
      icon: GraduationCap,
      shouldShow: selectedPartner?.docsendUrl,
      onClick: () => navigateToExternalUrl(selectedPartner?.docsendUrl),
    },
    {
      name: 'Schedule a Meeting',
      icon: CalendarClock,
      shouldShow: true,
      onClick: () =>
        setFrameDialog({
          isOpen: true,
          title: 'Schedule a Meeting',
          url: selectedPartner?.dosCalendly || DEFAULT_LINKS.scheduleMeeting,
        }),
      keepMenuOpen: true,
    },
  ];

  return (
    <>
      <Divider />
      <Box>
        {items
          .filter((item) => item.shouldShow)
          .map((item) => {
            const Icon = item.icon;

            return (
              <MenuItem
                key={item.name}
                className={v2MenuItemSizeClass.large}
                onClick={() => {
                  item.onClick();
                  if (!item.keepMenuOpen) onCloseMenu?.();
                }}
              >
                <ListItemIcon>
                  <Icon />
                </ListItemIcon>
                <ListItemText>{item.name}</ListItemText>
              </MenuItem>
            );
          })}
        <FramePopover
          anchor={anchor}
          title={frameDialog.title}
          isOpen={frameDialog.isOpen}
          onClose={() => setFrameDialog({ ...frameDialog, isOpen: false })}
          url={frameDialog.url}
        />
      </Box>
      <Divider />
    </>
  );
};
